import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { CheckCircle, AlertTriangle } from 'lucide-react';

import ClientTable from './components/ClientTable';
import ClientModal from './components/ClientModal';
import ClientStats from './components/ClientStats';
import { API_ENDPOINTS } from '../../utils/config';

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [successMessage, setSuccessMessage] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Fetch clients from API
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setIsLoadingClients(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers });
      const clientsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || response.data?.data || response.data?.clients || []);
      
      setClients(clientsData);
    } catch (error) {
      // Failed to fetch clients handled silently
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const totalClients = clients?.length;
    const activeClients = clients?.filter(c => c?.status === 'Active' || c?.is_active === true)?.length;
    const inactiveClients = clients?.filter(c => c?.status === 'Inactive' || c?.is_active === false)?.length;
    const newThisMonth = clients?.filter(c => {
      const createdDate = new Date(c.created_at || c.createdAt);
      const currentDate = new Date();
      return createdDate?.getMonth() === currentDate?.getMonth() && 
             createdDate?.getFullYear() === currentDate?.getFullYear();
    })?.length;

    return {
      totalClients,
      activeClients,
      inactiveClients,
      newThisMonth,
      clientsChange: 0,
      activeChange: 0,
      inactiveChange: 0,
      newChange: 0
    };
  }, [clients]);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = clients;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter(client =>
        client?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        client?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        client?.contact_person?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        client?.branch?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'lastInteraction') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [clients, searchTerm, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAddClient = () => {
    setModalMode('add');
    setEditingClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (client) => {
    setModalMode('edit');
    setEditingClient(client);
    setIsModalOpen(true);
  };

  const handleSaveClient = async (clientData) => {
    // Refresh the clients list after save
    await fetchClients();
    
    // Show success message
    const message = modalMode === 'add' 
      ? 'Client created successfully!' 
      : 'Client updated successfully!';
    setSuccessMessage(message);
    
    // Clear message after 3 seconds
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        API_ENDPOINTS.CLIENTS_DELETE(clientToDelete?.id),
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      
      setSuccessMessage(`Client "${clientToDelete?.name}" deleted successfully.`);
      setTimeout(() => setSuccessMessage(''), 3000);
      await fetchClients();
      setIsDeleteModalOpen(false);
      setClientToDelete(null);
    } catch (error) {
      setSuccessMessage('');
      alert('Failed to delete client. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      await Promise.all(selectedClients.map(id => axios.delete(API_ENDPOINTS.CLIENTS_DELETE(id), { headers })));
      setSuccessMessage(`${selectedClients.length} client(s) deleted successfully.`);
      setTimeout(() => setSuccessMessage(''), 3000);
      setSelectedClients([]);
      setIsBulkDeleteModalOpen(false);
      await fetchClients();
    } catch (error) {
      alert('Failed to delete some clients. Please try again.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setClientToDelete(null);
  };

  const handleBulkImport = () => {
    setIsBulkImportModalOpen(true);
    setSelectedFile(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx')) {
        alert('Please select an XLSX file');
        event.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleImportSubmit = async () => {
    if (!selectedFile) {
      alert('Please select a file to import');
      return;
    }

    setIsImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        API_ENDPOINTS.CLIENTS_BULK_IMPORT,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );

      // Close modal
      setIsBulkImportModalOpen(false);
      setSelectedFile(null);

      // Show success message with detailed counts
      const { imported_count, failed_count, total_rows, errors } = response.data;
      let message = `${imported_count} rows imported successfully!`;
      if (failed_count > 0) {
        message += ` (${failed_count} failed out of ${total_rows} total rows)`;
      }
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 5000);

      // Refresh the clients list
      await fetchClients();

      // Show errors if any
      if (errors && errors.length > 0) {
        // Import errors handled silently
      }
    } catch (error) {
      // Failed to import clients handled silently
      const errorMsg = error?.response?.data?.message || 
                       error?.response?.data?.detail || 
                       'Failed to import clients. Please try again.';
      alert(errorMsg);
    } finally {
      setIsImporting(false);
    }
  };

  const cancelBulkImport = () => {
    setIsBulkImportModalOpen(false);
    setSelectedFile(null);
  };

  const handleBulkExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(
        API_ENDPOINTS.CLIENTS_BULK_EXPORT,
        { 
          headers,
          responseType: 'blob'
        }
      );

      // Get filename from content-disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'clients_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      // Show success message with row count
      const rowCount = clients.length;
      setSuccessMessage(`${rowCount} rows exported successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      // Failed to export clients handled silently
      setSuccessMessage('');
      alert('Failed to export clients. Please try again.');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(
        API_ENDPOINTS.CLIENTS_DOWNLOAD_TEMPLATE,
        { 
          headers,
          responseType: 'blob'
        }
      );

      // Get filename from content-disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'client_template.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      // Show success message
      setSuccessMessage('Client template downloaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      // Failed to download template handled silently
      setSuccessMessage('');
      
      let errorMessage = 'Failed to download template. ';
      if (error?.response?.status === 404) {
        errorMessage += 'Template not found on server.';
      } else if (error?.response?.status === 401 || error?.response?.status === 403) {
        errorMessage += 'Authentication required.';
      } else if (error?.response?.status === 500) {
        errorMessage += 'Server error occurred.';
      } else if (error?.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Client Management</h1>
              <p className="text-muted-foreground">
                Manage your client relationships and track communication history
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3 mt-6 lg:mt-0">
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={handleBulkImport}
                className="flex-1 sm:flex-none"
              >
                Bulk Import
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleBulkExport}
                className="flex-1 sm:flex-none"
              >
                Bulk Export
              </Button>
              <Button
                variant="outline"
                iconName="FileDown"
                iconPosition="left"
                onClick={handleDownloadTemplate}
                className="flex-1 sm:flex-none"
              >
                Download Template
              </Button>
              <Button
                variant="default"
                iconName="UserPlus"
                iconPosition="left"
                onClick={handleAddClient}
                className="flex-1 sm:flex-none"
              >
                Add Client
              </Button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 rounded-lg border border-green-300 bg-green-50 text-green-700">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Statistics Cards */}
          <ClientStats stats={stats} className="mb-8" />

          {/* Main Content */}
          <div className="space-y-6">
            {/* Client Table */}
            {selectedClients.length > 0 && (
              <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg px-4 py-3">
                <span className="text-sm font-medium text-foreground">
                  {selectedClients.length} client{selectedClients.length !== 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedClients([])}>
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setIsBulkDeleteModalOpen(true)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Delete Selected ({selectedClients.length})
                  </Button>
                </div>
              </div>
            )}
            <ClientTable
              clients={filteredAndSortedClients}
              onClientEdit={handleEditClient}
              onClientView={handleEditClient}
              onClientDelete={handleDeleteClient}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              sortConfig={sortConfig}
              onSort={handleSort}
              selectedClients={selectedClients}
              onSelectionChange={setSelectedClients}
            />
          </div>
        </div>
      </main>
      {/* Client Modal */}
      <ClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        client={editingClient}
        onSave={handleSaveClient}
        mode={modalMode}
      />

      {/* Bulk Delete Confirmation Modal */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-md shadow-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Delete {selectedClients.length} Client{selectedClients.length !== 1 ? 's' : ''}</h3>
                  <p className="text-sm text-muted-foreground mt-1">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-foreground mb-2">
                Are you sure you want to delete <span className="font-semibold">{selectedClients.length} selected client{selectedClients.length !== 1 ? 's' : ''}</span>?
              </p>
              <p className="text-sm text-muted-foreground mb-6">All associated data will be permanently removed from the system.</p>
              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setIsBulkDeleteModalOpen(false)} disabled={isBulkDeleting}>Cancel</Button>
                <Button
                  onClick={confirmBulkDelete}
                  disabled={isBulkDeleting}
                  loading={isBulkDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {isBulkDeleting ? 'Deleting...' : `Delete ${selectedClients.length} Client${selectedClients.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-md shadow-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Delete Client</h3>
                  <p className="text-sm text-muted-foreground mt-1">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-foreground">
                  Are you sure you want to delete <span className="font-semibold">"{clientToDelete?.name}"</span>?
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  All associated data will be permanently removed from the system.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelDelete}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  loading={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Client'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {isBulkImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-md shadow-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Bulk Import Clients</h3>
                  <p className="text-sm text-muted-foreground mt-1">Upload an XLSX file to import clients</p>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select XLSX File
                </label>
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-foreground
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary file:text-primary-foreground
                    hover:file:bg-primary/90
                    file:cursor-pointer cursor-pointer
                    border border-input rounded-md"
                />
                {selectedFile && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Selected: <span className="font-medium text-foreground">{selectedFile.name}</span>
                  </p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Download the template first if you haven't already.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelBulkImport}
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={handleImportSubmit}
                  disabled={isImporting || !selectedFile}
                  loading={isImporting}
                  iconName="Upload"
                  iconPosition="left"
                >
                  {isImporting ? 'Importing...' : 'Import'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;